import React, { useState, useEffect } from 'react';
import { API_URL } from "../config/api";
import { 
  Package, DollarSign, MapPin, Users, TrendingUp, CreditCard, 
  UserCheck, Smartphone, Mail, Code, Wrench, Check, Star,
  Zap, Shield, ArrowRight
} from 'lucide-react';


const iconMap = {
  Package: Package,
  DollarSign: DollarSign,
  MapPin: MapPin,
  Users: Users,
  TrendingUp: TrendingUp,
  CreditCard: CreditCard,
  UserCheck: UserCheck,
  Smartphone: Smartphone,
  Mail: Mail,
  Code: Code,
  Wrench: Wrench,
};

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedModules, setSelectedModules] = useState([]);
  const [modules, setModules] = useState([]);
  const [bundles, setBundles] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await fetch(`${API_URL}/subscriptions/modules`);
      const data = await response.json();
      setModules(data.modules || []);
      setBundles(data.bundles || {});
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    if (moduleId === 'tools') return; // Tools is always free
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const selectBundle = (bundleModules) => {
    setSelectedModules(bundleModules);
  };

  const calculateTotal = () => {
    return selectedModules.reduce((total, moduleId) => {
      const module = modules.find(m => m.id === moduleId);
      if (!module) return total;
      return total + (billingCycle === 'yearly' ? module.price_yearly_eur : module.price_monthly_eur);
    }, 0);
  };

  const handleCheckout = async () => {
    if (selectedModules.length === 0) return;
    
    setCheckoutLoading(true);
    try {
      const response = await fetch(`${API_URL}/subscriptions/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modules: selectedModules,
          billing_cycle: billingCycle
        })
      });
      
      const data = await response.json();
      
      if (data.stripe_checkout_url) {
        window.location.href = data.stripe_checkout_url;
      } else {
        alert('Checkout created. Contact sales@europacific.com to complete.');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      alert('Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Package;
    return <IconComponent className="w-6 h-6" />;
  };

  const categoryColors = {
    operations: 'from-blue-500 to-blue-600',
    sales: 'from-green-500 to-green-600',
    finance: 'from-purple-500 to-purple-600',
    hr: 'from-orange-500 to-orange-600',
    customer: 'from-cyan-500 to-cyan-600',
    tools: 'from-gray-500 to-gray-600',
    developer: 'from-red-500 to-red-600',
    base: 'from-emerald-500 to-emerald-600',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm mb-6">
            <Zap className="w-4 h-4" />
            Modular SaaS Platform
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Build Your Perfect
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"> Logistics Stack</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            Choose only the modules you need. Scale up anytime. No commitments on unused features.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-slate-800 p-1 rounded-full">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' 
                  ? 'bg-white text-slate-900' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">Save 17%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bundles */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-yellow-400" />
          Popular Bundles
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {Object.entries(bundles).map(([key, bundle]) => (
            <div 
              key={key}
              onClick={() => selectBundle(bundle.modules)}
              className={`bg-slate-800/50 backdrop-blur border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                JSON.stringify(selectedModules.sort()) === JSON.stringify(bundle.modules.sort())
                  ? 'border-blue-500 ring-2 ring-blue-500/50'
                  : 'border-slate-700 hover:border-slate-600'
              }`}
            >
              <h3 className="text-lg font-semibold text-white mb-2">{bundle.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{bundle.description}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-white">
                  €{billingCycle === 'yearly' ? bundle.price_yearly_eur : bundle.price_monthly_eur}
                </span>
                <span className="text-slate-400">/{billingCycle === 'yearly' ? 'year' : 'mo'}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {bundle.modules.map(m => (
                  <span key={m} className="bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Individual Modules */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6">All Modules</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map(module => {
            const isSelected = selectedModules.includes(module.id);
            const isFree = module.price_monthly_eur === 0;
            const price = billingCycle === 'yearly' ? module.price_yearly_eur : module.price_monthly_eur;
            
            return (
              <div
                key={module.id}
                onClick={() => toggleModule(module.id)}
                className={`relative bg-slate-800/50 backdrop-blur border rounded-xl p-6 cursor-pointer transition-all hover:scale-[1.02] ${
                  isSelected 
                    ? 'border-blue-500 ring-2 ring-blue-500/50' 
                    : isFree 
                      ? 'border-emerald-500/50'
                      : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4 bg-blue-500 rounded-full p-1">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                
                {/* Free badge */}
                {isFree && (
                  <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full">
                    FREE
                  </div>
                )}

                {/* Icon & Title */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${categoryColors[module.category] || categoryColors.tools} mb-4`}>
                  {getIcon(module.icon)}
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-1">{module.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{module.description}</p>
                
                {/* Price */}
                <div className="flex items-baseline gap-1 mb-4">
                  {isFree ? (
                    <span className="text-2xl font-bold text-emerald-400">Included</span>
                  ) : (
                    <>
                      <span className="text-2xl font-bold text-white">€{price}</span>
                      <span className="text-slate-400">/{billingCycle === 'yearly' ? 'year' : 'mo'}</span>
                    </>
                  )}
                </div>
                
                {/* Features */}
                <ul className="space-y-2">
                  {module.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sticky Checkout Bar */}
      {selectedModules.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur border-t border-slate-700 p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">
                {selectedModules.length} module{selectedModules.length > 1 ? 's' : ''} selected
              </p>
              <p className="text-white text-2xl font-bold">
                €{calculateTotal()} <span className="text-slate-400 text-base font-normal">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
              </p>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
            >
              {checkoutLoading ? (
                <>Processing...</>
              ) : (
                <>
                  Subscribe Now
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex flex-wrap justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span>Secure Payments via Stripe</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span>Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            <span>Instant Access</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
